import React from "react";
import lionImg from "../assets/lion.png";
import fullLogoImg from "../assets/logo-full.png";

/**
 * Chespi Group brand assets — the official heraldic lion and the framed
 * "CHESPI · GROUP" wordmark, extracted from the brand artwork and rendered on
 * transparent backgrounds so they sit cleanly on the deep-black UI.
 */

// The lion crest alone — square. Used as a compact mark and as a watermark.
export function LionMark({ size = 56, className = "", style }) {
  return (
    <img
      src={lionImg}
      width={size}
      height={size}
      alt="Chespi Group lion crest"
      className={className}
      style={{ display: "block", ...style }}
      draggable={false}
    />
  );
}

// The full framed wordmark logo (CHESPI · lion · GROUP).
export function FullLogo({ height = 56, className = "", style }) {
  return (
    <img
      src={fullLogoImg}
      height={height}
      alt="Chespi Group"
      className={className}
      style={{ display: "block", height, width: "auto", ...style }}
      draggable={false}
    />
  );
}

// Corsa Exchange logo — the brand "C" monogram (the very same Georgia-serif
// letterform as the site favicon, favicon.svg) stacked over a "CORSA EXCHANGE"
// wordmark in that same serif. Rendered solid white on a transparent background so
// it sits on the burgundy (#5a1a14) Corsa brand tile / dark UI.
export function CorsaLogo({ className = "" }) {
  return (
    <div
      className={`inline-flex flex-col items-center leading-none text-white ${className}`}
      role="img"
      aria-label="Corsa Exchange"
    >
      {/* The Corsa "C" monogram (vectorised Georgia letterform, geometrically centered
          in a square) inside a fine ring — so it sits dead-center in the circle. */}
      <span className="flex aspect-square w-28 items-center justify-center rounded-full border-2 border-white/55 sm:w-32 lg:w-36">
        <img src={`${process.env.PUBLIC_URL}/corsa-c.svg`} alt="" draggable={false} className="w-[58%]" />
      </span>
      {/* Two-tier wordmark — "CORSA" spread out over a small, widely-tracked white
          "EXCHANGE", both optically centered (pl == tracking cancels the trailing letter-space). */}
      <span className="mt-4 inline-block pl-[0.14em] font-serif text-3xl font-bold uppercase leading-none tracking-[0.14em] sm:text-4xl">
        Corsa
      </span>
      <span className="mt-1 inline-block pl-[0.5em] text-[0.5rem] font-medium uppercase leading-none tracking-[0.5em] text-white sm:text-[0.55rem] lg:text-[0.6rem]">
        Exchange
      </span>
    </div>
  );
}

// Taurus Commercial Developments lockup — the brand artwork (horned skyline emblem +
// "TAURUS" + "COMMERCIAL DEVELOPMENTS") recoloured all-white on a transparent
// background so it sits cleanly centered over the dark commercial card photo.
export function TaurusLogo({ className = "" }) {
  return (
    <img
      src={`${process.env.PUBLIC_URL}/TaurusLogoWhite.svg`}
      alt="Taurus Commercial Developments"
      loading="lazy"
      draggable={false}
      className={`block w-36 sm:w-40 lg:w-52 ${className}`}
    />
  );
}

// Meliora Luxury Residences lockup — the brand artwork (arched skyline "M" monogram +
// "MELIORA" + "LUXURY RESIDENCES") cropped from its source, recoloured all-white on a
// transparent background so it sits cleanly centered over the warm-brown residential card.
export function MelioraLogo({ className = "" }) {
  return (
    <img
      src={`${process.env.PUBLIC_URL}/MelioraLogoWhite.svg`}
      alt="Meliora Luxury Residences"
      loading="lazy"
      draggable={false}
      // Phone: fills almost the whole tile (84vw, safe from clipping on every device).
      // From md up (grid becomes 2-col, tile narrower) it drops to a fixed size.
      className={`block w-[84vw] md:w-72 ${className}`}
    />
  );
}

// Framed Chespi sub-brand lockup — the navbar group logo recoloured all-white and
// reworded: "CHESPI · lion · {word}", a single bold outline, wide-and-short with tight
// padding, the white lion crest centered between the serif caps words. For dark cards.
function ChespiFramedLogo({
  word,
  markSize = 28,
  textClass = "text-sm lg:text-base",
  frameClass = "border-2 px-1.5 py-0.5 sm:px-2",
  gapClass = "gap-1 sm:gap-1.5",
  trackingClass = "tracking-[0.18em]",
  // Colour treatment — defaults to the all-white lockup for dark cards. Override
  // (e.g. gold text + border + the lion's native gold) for light backgrounds.
  textColorClass = "text-white",
  borderColorClass = "border-white",
  lionFilterClass = "brightness-0 invert",
  lionClass = "",
  className = "",
}) {
  return (
    <div
      className={`inline-flex items-center justify-center whitespace-nowrap ${borderColorClass} ${gapClass} ${frameClass} ${className}`}
    >
      <span className={`font-serif font-medium leading-none ${textColorClass} ${trackingClass} ${textClass}`}>CHESPI</span>
      <LionMark size={markSize} className={`shrink-0 ${lionFilterClass} ${lionClass}`} />
      <span className={`font-serif font-medium leading-none ${textColorClass} ${trackingClass} ${textClass}`}>{word}</span>
    </div>
  );
}

// Energy card lockup — CHESPI · lion · ENERGY.
export function ChespiEnergyLogo(props) {
  return <ChespiFramedLogo word="ENERGY" {...props} />;
}

// Tourism & Hospitality card lockup — CHESPI · lion · HOSPITALITY.
export function ChespiHospitalityLogo(props) {
  return <ChespiFramedLogo word="HOSPITALITY" {...props} />;
}

// Retail card lockup — CHESPI · lion · RETAIL.
export function ChespiRetailLogo(props) {
  return <ChespiFramedLogo word="RETAIL" {...props} />;
}

// Farming card lockup — CHESPI · lion · FARMING.
export function ChespiFarmingLogo(props) {
  return <ChespiFramedLogo word="FARMING" {...props} />;
}

// Navbar / footer lockup. `markSize` kept for call-site compatibility and maps
// to the rendered logo height; `stacked` shows the lion above the wordmark.
export function LogoLockup({ markSize = 40, stacked = false, className = "" }) {
  if (stacked) {
    return (
      <div className={`flex flex-col items-center gap-4 ${className}`}>
        <LionMark size={markSize * 1.7} />
        <div className="text-center leading-none">
          <span className="block font-serif text-2xl tracking-[0.42em] text-gold-leaf">CHESPI</span>
          <span className="mt-1.5 block font-serif text-xs tracking-[0.62em] text-white/55">GROUP</span>
        </div>
      </div>
    );
  }
  return <FullLogo height={markSize + 8} className={className} />;
}
